// Pure helpers for the universal entity form: initial value derivation, payload
// builders, and add/update action dispatch. No React, no component state.
// entity-form.tsx re-exports FormTarget from here.
import { todayIso } from "../lib/date";
import type { LifeOsApi } from "../lib/life-os-contract";
import { DEFAULT_ACCOUNT_ID } from "../lib/storage";
import { buildRrule } from "./event-recurrence-fields";
import type { RecurrenceState } from "./event-recurrence-fields";
import type { EntityKind } from "./entity-form-config";

export type FormTarget = { kind: EntityKind; item?: Record<string, unknown> } | null;

const DEFAULTS: Record<EntityKind, Record<string, unknown>> = {
  task: { description: "", dueDate: "", deferDate: "", priority: "medium", estimateMinutes: 30, status: "todo", tags: "", projectId: "", goalId: "" },
  project: { area: "", deadline: "", progress: 0, status: "active", nextAction: "", goalId: "" },
  goal: { objective: "", horizon: "quarter", metric: "", unit: "", current: 0, target: 10, deadline: "" },
  habit: { targetPerWeek: 5, color: "#7c3aed", linkedGoalId: "" },
  note: { tags: "", pinned: false, category: "", linkedProjectId: "" },
  transaction: { type: "expense", date: "", category: "", note: "", accountId: "" },
  budget: { monthlyLimit: 0, rollover: false, alertThreshold: 80 },
  event: { date: "", time: "09:00", endTime: "", durationMin: "", color: "#7c3aed" },
  recurring: { type: "income", category: "", amount: 0, frequency: "monthly", dayOfMonth: 1, endOfMonth: false, endDate: "", note: "", accountId: "" },
  // D38: currency is now selectable (defaults to VND)
  account: { type: "cash", openingBalance: 0, currency: "VND" },
};

export function initialValues(target: NonNullable<FormTarget>): Record<string, unknown> {
  const base = { ...DEFAULTS[target.kind] };
  if (!target.item) {
    if ("dueDate" in base) base.dueDate = todayIso();
    if ("date" in base) base.date = todayIso();
    return base;
  }
  const item = target.item;
  const v: Record<string, unknown> = { ...base, ...item };
  if ((target.kind === "note" || target.kind === "task") && Array.isArray(item.tags)) v.tags = (item.tags as string[]).join(", ");
  return v;
}

const num = (v: unknown) => Number(v) || 0;
const str = (v: unknown) => String(v ?? "").trim();

export function buildPayload(kind: EntityKind, v: Record<string, unknown>, recurrence?: RecurrenceState, reminders?: number[]): Record<string, unknown> {
  switch (kind) {
    case "task": {
      // D27: build rrule from recurrence state; undefined clears it (series editor only on parent)
      const taskRrule = recurrence ? buildRrule(recurrence) : undefined;
      // Emit undefined when cleared so edit patches can remove stale links.
      const taskProjectId = str(v.projectId) || undefined;
      const taskGoalId = str(v.goalId) || undefined;
      return {
        title: str(v.title),
        description: str(v.description) || undefined,
        dueDate: str(v.dueDate) || todayIso(),
        deferDate: str(v.deferDate) || undefined,
        status: v.status,
        priority: v.priority,
        estimateMinutes: num(v.estimateMinutes),
        tags: str(v.tags).split(",").map((t) => t.trim()).filter(Boolean),
        // Always emit recurrence so edits can clear it (undefined removes the field via patch)
        recurrence: taskRrule,
        projectId: taskProjectId,
        goalId: taskGoalId,
      };
    }
    case "project": {
      const goalId = str(v.goalId) || undefined;
      return { name: str(v.name), area: str(v.area), deadline: str(v.deadline) || todayIso(), progress: Math.min(100, num(v.progress)), status: v.status, nextAction: str(v.nextAction), goalId };
    }
    case "goal": {
      // Nhập sẵn "hiện tại" > 0 → người dùng muốn theo dõi bằng chỉ số tay; đặt
      // progressMode "manual" để auto-rollup (TB dự án) không nuốt mất giá trị đó.
      const goalCurrent = num(v.current);
      return { title: str(v.title), objective: str(v.objective) || undefined, horizon: v.horizon, metric: str(v.metric), unit: str(v.unit) || undefined, current: goalCurrent, target: Math.max(1, num(v.target)), deadline: str(v.deadline) || todayIso(), status: "active", ...(goalCurrent > 0 ? { progressMode: "manual" as const } : {}) };
    }
    case "habit": {
      const linkedGoalId = str(v.linkedGoalId) || undefined;
      return { name: str(v.name), targetPerWeek: Math.min(7, Math.max(1, num(v.targetPerWeek))), color: str(v.color) || "#7c3aed", linkedGoalId };
    }
    case "note": {
      const noteCategory = str(v.category) || undefined;
      const noteLinkedProjectId = str(v.linkedProjectId) || undefined;
      return {
        title: str(v.title),
        tags: str(v.tags).split(",").map((t) => t.trim()).filter(Boolean),
        body: str(v.body),
        pinned: Boolean(v.pinned),
        category: noteCategory,
        linkedProjectId: noteLinkedProjectId,
      };
    }
    case "transaction": {
      // accountId: use selected value; fall back to DEFAULT_ACCOUNT_ID if blank
      const accountId = str(v.accountId) || DEFAULT_ACCOUNT_ID;
      return { type: v.type, category: str(v.category), amount: num(v.amount), date: str(v.date) || todayIso(), note: str(v.note), accountId };
    }
    case "budget": return { category: str(v.category), monthlyLimit: num(v.monthlyLimit), rollover: Boolean(v.rollover), alertThreshold: Math.min(100, Math.max(1, num(v.alertThreshold) || 80)) };
    case "event": {
      // Build rrule from recurrence state (undefined when freq=none)
      const rrule = recurrence ? buildRrule(recurrence) : undefined;
      // durationMin: prefer explicit value; derive from endTime if only that is set
      const rawDuration = str(v.durationMin);
      const endTimeStr = str(v.endTime);
      let durationMin: number | undefined;
      if (rawDuration !== "" && num(v.durationMin) > 0) {
        durationMin = num(v.durationMin);
      } else if (endTimeStr) {
        // Derive durationMin from startTime → endTime difference
        const startTime = str(v.time) || "09:00";
        durationMin = timeDiffMinutes(startTime, endTimeStr);
      }
      return {
        title: str(v.title),
        date: str(v.date) || todayIso(),
        time: str(v.time) || "09:00",
        color: str(v.color) || "#7c3aed",
        ...(endTimeStr ? { endTime: endTimeStr } : {}),
        ...(durationMin !== undefined && durationMin > 0 ? { durationMin } : {}),
        // Always emit these so editing can CLEAR them (shallow-merge patch would
        // otherwise keep stale values when the user removes recurrence/reminders).
        rrule: rrule || undefined,
        exdates: Array.isArray(v.exdates) ? (v.exdates as string[]) : undefined,
        reminders: reminders ?? [], // D10; [] clears existing reminders on edit
      };
    }
    case "recurring": {
      // D49: chỉ nhận tần suất hợp lệ, mặc định monthly (back-compat).
      const freq = (["monthly", "weekly", "quarterly", "yearly"] as const).includes(v.frequency as never) ? v.frequency : "monthly";
      return {
        type: v.type,
        category: str(v.category),
        amount: num(v.amount),
        frequency: freq,
        dayOfMonth: Math.min(28, Math.max(1, num(v.dayOfMonth) || 1)),
        endOfMonth: Boolean(v.endOfMonth),
        note: str(v.note),
        startDate: str(v.startDate) || todayIso(),
        ...(str(v.endDate) ? { endDate: str(v.endDate) } : {}),
        ...(str(v.accountId) ? { accountId: str(v.accountId) } : {}),
      };
    }
    // D4/D38: openingBalance may be negative (credit cards). currency from select (default VND).
    case "account": {
      const currency = str(v.currency) || "VND";
      return { name: str(v.name), type: v.type ?? "cash", openingBalance: num(v.openingBalance), currency };
    }
  }
}

/** Calculate minute difference between two HH:MM strings. Returns 0 if invalid or negative. */
function timeDiffMinutes(start: string, end: string): number {
  const toMins = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    return (isNaN(h) || isNaN(m)) ? -1 : h * 60 + m;
  };
  const diff = toMins(end) - toMins(start);
  return diff > 0 ? diff : 0;
}

export function addDispatch(api: LifeOsApi, kind: EntityKind, p: Record<string, unknown>) {
  const a = api.actions as unknown as Record<string, (x: unknown) => void>;
  const map: Record<EntityKind, string> = {
    task: "addTask", project: "addProject", goal: "addGoal", habit: "addHabit",
    note: "addNote", transaction: "addTransaction", budget: "addBudget",
    event: "addEvent", recurring: "addRecurring", account: "addAccount",
  };
  a[map[kind]](p);
}

export function updateDispatch(api: LifeOsApi, kind: EntityKind, id: string, p: Record<string, unknown>) {
  const a = api.actions as unknown as Record<string, (id: string, patch: unknown) => void>;
  const map: Record<EntityKind, string> = {
    task: "updateTask", project: "updateProject", goal: "updateGoal", habit: "updateHabit",
    note: "updateNote", transaction: "updateTransaction", budget: "updateBudget",
    event: "updateEvent", recurring: "updateRecurring", account: "updateAccount",
  };
  a[map[kind]](id, p);
}
